import React, { useEffect, useState } from "react";
import { Loader2, FileCode2, FlaskConical, FileText, Download, ShieldAlert, CheckCircle2, GitCommitVertical, Eye, Save, LayoutGrid, ArrowRightLeft } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { api } from "@/lib/api";
import { SeverityBadge, FieldConfidenceBadge } from "@/components/common/Badges";
import { TID } from "@/lib/testIds";
import { toast } from "sonner";
import { DiagramsTab } from "@/components/roles/DiagramsTab";
import { BestPracticeTab } from "@/components/roles/BestPracticeTab";

const RELEASE_STYLES = {
    Deprecated: "bg-rose-500/15 text-rose-300 border-rose-500/30",
    "Available with Successor": "bg-amber-500/15 text-amber-300 border-amber-500/30",
    Available: "bg-emerald-500/10 text-emerald-300 border-emerald-500/25",
    "Not in Fiori Library": "bg-white/5 text-slate-400 border-white/10",
};

const ACTVT_OPTIONS = [
    { code: "01", label: "Create" },
    { code: "02", label: "Change" },
    { code: "03", label: "Display" },
    { code: "06", label: "Delete" },
    { code: "16", label: "Execute" },
    { code: "43", label: "Release" },
];

/** Authorization Matrix Manager per-role tool drawer: org-level field
 * mapping, a Field Editor to capture REAL ACTVT/org values (Field-Value Role
 * Provisioning), T-code addition simulation, .SAP reference export, BPD/PDD
 * document generation (with in-app preview), release readiness. */
export function RoleToolsModal({ role, open, onOpenChange, onUpdated, initialTab }) {
    const [orgFields, setOrgFields] = useState(null);
    const [loadingOrg, setLoadingOrg] = useState(false);
    const [release, setRelease] = useState(null);
    const [loadingRelease, setLoadingRelease] = useState(false);
    const [addTcodes, setAddTcodes] = useState("");
    const [simResult, setSimResult] = useState(null);
    const [simulating, setSimulating] = useState(false);
    const [exportingSap, setExportingSap] = useState(false);
    const [generatingDoc, setGeneratingDoc] = useState("");
    const [preview, setPreview] = useState(null); // { docType, content }
    const [fieldReq, setFieldReq] = useState(null);
    const [loadingFieldReq, setLoadingFieldReq] = useState(false);
    const [fieldEdits, setFieldEdits] = useState({}); // {auth_object: {ACTVT: Set<string>, [orgField]: string}}
    const [savingFields, setSavingFields] = useState(false);
    const [fieldImpact, setFieldImpact] = useState(null);
    const [migrating, setMigrating] = useState(false);
    const [migrationResult, setMigrationResult] = useState(null);

    useEffect(() => {
        if (open && role) {
            setSimResult(null);
            setAddTcodes("");
            setPreview(null);
            setFieldImpact(null);
            setMigrationResult(null);
            setLoadingOrg(true);
            setLoadingRelease(true);
            setLoadingFieldReq(true);
            api.get(`/roles/${role.id}/org-fields`).then(({ data }) => setOrgFields(data))
                .catch(() => setOrgFields(null))
                .finally(() => setLoadingOrg(false));
            api.get(`/roles/${role.id}/release-readiness`).then(({ data }) => setRelease(data))
                .catch(() => setRelease(null))
                .finally(() => setLoadingRelease(false));
            api.get(`/roles/${role.id}/field-requirements`).then(({ data }) => {
                setFieldReq(data);
                const initial = {};
                data.breakdown.forEach((b) => {
                    initial[b.auth_object] = {
                        ACTVT: new Set(b.captured?.ACTVT || []),
                        ...Object.fromEntries(
                            b.org_fields.filter((f) => f.org_level).map((f) => [f.field, (b.captured?.[f.field] || [])[0] || ""])
                        ),
                    };
                });
                setFieldEdits(initial);
            })
                .catch(() => setFieldReq(null))
                .finally(() => setLoadingFieldReq(false));
        }
    }, [open, role]);

    const toggleActvt = (obj, code) => {
        setFieldEdits((prev) => {
            const entry = prev[obj] || { ACTVT: new Set() };
            const next = new Set(entry.ACTVT);
            next.has(code) ? next.delete(code) : next.add(code);
            return { ...prev, [obj]: { ...entry, ACTVT: next } };
        });
    };

    const setOrgFieldValue = (obj, field, value) => {
        setFieldEdits((prev) => ({ ...prev, [obj]: { ...(prev[obj] || { ACTVT: new Set() }), [field]: value } }));
    };

    const saveFieldValues = async () => {
        setSavingFields(true);
        try {
            const field_values = {};
            Object.entries(fieldEdits).forEach(([obj, entry]) => {
                const payload = {};
                if (entry.ACTVT?.size) payload.ACTVT = Array.from(entry.ACTVT);
                Object.entries(entry).forEach(([k, v]) => {
                    if (k !== "ACTVT" && typeof v === "string" && v.trim()) payload[k] = [v.trim()];
                });
                if (Object.keys(payload).length) field_values[obj] = payload;
            });
            const { data } = await api.put(`/roles/${role.id}/field-values`, { field_values });
            setFieldImpact(data.impact);
            const { conflicts_cleared, critical_cleared, conflicts_after, critical_after } = data.impact;
            if (conflicts_cleared || critical_cleared) {
                toast.success(`Field-verified: ${conflicts_cleared + critical_cleared} violation(s) CLEARED based on real ACTVT values`);
            } else {
                toast.success(`Field values saved · ${conflicts_after + critical_after} violation(s) remain confirmed`);
            }
            onUpdated?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to save field values");
        } finally { setSavingFields(false); }
    };

    const runSimulation = async () => {
        const codes = addTcodes.split(/[;,\s]+/).map((t) => t.trim()).filter(Boolean);
        if (codes.length === 0) { toast.error("Enter at least one T-code"); return; }
        setSimulating(true);
        try {
            const { data } = await api.post(`/roles/${role.id}/simulate`, { additional_tcodes: codes });
            setSimResult(data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Simulation failed");
        } finally { setSimulating(false); }
    };

    const downloadBlob = (data, filename) => {
        const url = window.URL.createObjectURL(new Blob([data]));
        const a = document.createElement("a");
        a.href = url; a.download = filename;
        document.body.appendChild(a); a.click(); a.remove();
        window.URL.revokeObjectURL(url);
    };

    const exportSap = async () => {
        setExportingSap(true);
        try {
            const res = await api.get(`/roles/${role.id}/export/sap`, { responseType: "blob" });
            downloadBlob(res.data, `${role.name}.sap`);
            toast.success("PFCG-style reference file downloaded");
        } catch { toast.error("Export failed"); } finally { setExportingSap(false); }
    };

    const previewDoc = async (docType) => {
        setGeneratingDoc(docType);
        setPreview(null);
        try {
            const { data } = await api.post(`/roles/${role.id}/document`, { doc_type: docType, preview: true });
            setPreview({ docType, content: data.content });
        } catch { toast.error("Document generation failed"); } finally { setGeneratingDoc(""); }
    };

    const downloadPreviewedDoc = async () => {
        if (!preview) return;
        setGeneratingDoc(`download-${preview.docType}`);
        try {
            const res = await api.post(
                `/roles/${role.id}/document`,
                { doc_type: preview.docType, content: preview.content },
                { responseType: "blob" },
            );
            downloadBlob(res.data, `${role.name}_${preview.docType.toUpperCase()}.docx`);
            toast.success(`${preview.docType.toUpperCase()} downloaded`);
        } catch { toast.error("Download failed"); } finally { setGeneratingDoc(""); }
    };

    const convertToCatalog = async () => {
        setMigrating(true);
        try {
            const { data } = await api.post(`/roles/${role.id}/convert-to-catalog`);
            setMigrationResult(data);
            toast.success(`Converted — ${data.coverage_pct}% of T-codes mapped to ${data.matched_catalogs.length} Fiori Catalog(s)`);
            onUpdated?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Migration failed");
        } finally { setMigrating(false); }
    };

    if (!role) return null;
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid={TID.roleToolsModal} className="bg-[var(--bg-base)] border border-cyan-500/20 text-slate-200 w-[min(1120px,94vw)] max-w-none max-h-[88vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="font-display text-white flex items-center gap-2">
                        <FileCode2 size={16} className="text-cyan-400" /> Authorization Matrix Manager — {role.name}
                    </DialogTitle>
                    <DialogDescription className="text-xs text-slate-500 font-mono">
                        Inspect and edit the authorization matrix, export the .SAP reference file, and generate SAP diagrams for this role.
                    </DialogDescription>
                </DialogHeader>

                <button
                    data-testid={TID.roleSapExportBtn(role.id)}
                    onClick={exportSap}
                    disabled={exportingSap}
                    className="inline-flex items-center gap-1.5 self-start px-3 py-1.5 panel text-xs font-mono text-slate-300 hover:text-cyan-300 hover:border-cyan-500/30 transition-colors disabled:opacity-50"
                >
                    {exportingSap ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
                    Download .SAP File (PFCG role flat-file export)
                </button>

                <Tabs defaultValue={initialTab || "org"}>
                    <TabsList className="bg-white/5 border border-white/10 flex-wrap h-auto">
                        <TabsTrigger data-testid={TID.roleOrgFieldsTab} value="org">Org-Level Fields</TabsTrigger>
                        <TabsTrigger data-testid={TID.roleFieldEditorTab} value="field-editor">Field Editor</TabsTrigger>
                        <TabsTrigger data-testid={TID.roleBestPracticeTab} value="best-practice">Best Practice</TabsTrigger>
                        <TabsTrigger data-testid={TID.roleSimulateTab} value="sim">Simulate Addition</TabsTrigger>
                        <TabsTrigger data-testid={TID.roleReleaseTab} value="release">Release Readiness</TabsTrigger>
                        <TabsTrigger data-testid={TID.roleMigrationTab} value="migration">Fiori Migration</TabsTrigger>
                        <TabsTrigger data-testid={TID.roleDiagramsTab} value="diagrams">Diagrams</TabsTrigger>
                        <TabsTrigger data-testid={TID.roleDocsTab} value="docs">BPD / PDD</TabsTrigger>
                    </TabsList>

                    <TabsContent value="field-editor" className="pt-3 space-y-3">
                        <p className="text-xs text-slate-500 font-mono">
                            Capture the REAL ACTVT / org-level values this role was actually granted. Saving
                            re-runs SoD detection: a generic object-name match can be <span className="text-emerald-400">confirmed (field-verified)</span> or <span className="text-cyan-300">cleared entirely</span> once the true activity is known.
                        </p>
                        {loadingFieldReq ? (
                            <div className="flex items-center gap-2 text-slate-500 text-xs font-mono py-4"><Loader2 size={13} className="animate-spin" /> Loading…</div>
                        ) : !fieldReq?.breakdown?.length ? (
                            <p className="text-sm text-slate-500 font-mono py-4 text-center">No authorization objects on this role yet.</p>
                        ) : (
                            <div className="space-y-3 max-h-[340px] overflow-y-auto">
                                {fieldReq.breakdown.map((b) => {
                                    const orgLevelFields = b.org_fields.filter((f) => f.org_level);
                                    return (
                                        <div key={b.auth_object} className="panel p-3 rounded-[4px] space-y-2">
                                            <div className="font-mono text-xs text-cyan-300">{b.auth_object}</div>
                                            {b.actvt_requirements.length > 0 && (
                                                <p className="text-[10px] text-slate-500 leading-relaxed">
                                                    Commonly required by: {b.actvt_requirements.map((r) => `${r.function} (ACTVT ${r.actvt.join(",")})`).join(" · ")}
                                                </p>
                                            )}
                                            <div className="flex flex-wrap gap-2">
                                                {ACTVT_OPTIONS.map((o) => (
                                                    <label key={o.code} className="inline-flex items-center gap-1.5 text-[11px] font-mono text-slate-300 cursor-pointer select-none">
                                                        <input
                                                            data-testid={TID.roleFieldActvtCheckbox(b.auth_object, o.code)}
                                                            type="checkbox"
                                                            checked={fieldEdits[b.auth_object]?.ACTVT?.has(o.code) || false}
                                                            onChange={() => toggleActvt(b.auth_object, o.code)}
                                                            className="accent-emerald-500"
                                                        />
                                                        {o.code} · {o.label}
                                                    </label>
                                                ))}
                                            </div>
                                            {orgLevelFields.length > 0 && (
                                                <div className="flex flex-wrap gap-2 pt-1">
                                                    {orgLevelFields.map((f) => (
                                                        <input
                                                            key={f.field}
                                                            data-testid={TID.roleFieldOrgInput(b.auth_object, f.field)}
                                                            value={fieldEdits[b.auth_object]?.[f.field] || ""}
                                                            onChange={(e) => setOrgFieldValue(b.auth_object, f.field, e.target.value)}
                                                            placeholder={`${f.field} (${f.label})`}
                                                            className="bg-[var(--bg-base)] border border-white/10 rounded-sm px-2 py-1 text-[11px] font-mono text-slate-200 focus:border-cyan-500/40 focus:outline-none w-40"
                                                        />
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                        {fieldImpact && (
                            <div className="panel-elevated p-2.5 text-[11px] font-mono text-slate-300 flex flex-wrap gap-3">
                                <span>SoD conflicts: {fieldImpact.conflicts_before} → {fieldImpact.conflicts_after}</span>
                                <span>Critical access: {fieldImpact.critical_before} → {fieldImpact.critical_after}</span>
                            </div>
                        )}
                        <button
                            data-testid={TID.roleFieldEditorSaveBtn}
                            onClick={saveFieldValues}
                            disabled={savingFields || !fieldReq?.breakdown?.length}
                            className="btn-primary sheen w-full inline-flex items-center justify-center gap-1.5 py-2 text-xs disabled:opacity-60"
                        >
                            {savingFields ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                            Save &amp; Re-Verify SoD
                        </button>
                    </TabsContent>

                    <TabsContent value="org" className="pt-3">
                        {loadingOrg ? (
                            <div className="flex items-center gap-2 text-slate-500 text-xs font-mono py-4"><Loader2 size={13} className="animate-spin" /> Loading…</div>
                        ) : !orgFields?.breakdown?.length ? (
                            <p className="text-sm text-slate-500 font-mono py-4 text-center">No authorization objects on this role yet.</p>
                        ) : (
                            <div className="space-y-2 max-h-[320px] overflow-y-auto">
                                <p className="text-[11px] text-slate-500 font-mono mb-1">{orgFields.org_field_count} organizational-level field(s) identified.</p>
                                {orgFields.breakdown.map((b) => (
                                    <div key={b.auth_object} className="panel p-2.5 rounded-[4px]">
                                        <div className="font-mono text-xs text-cyan-300 mb-1">{b.auth_object}</div>
                                        <div className="flex flex-wrap gap-1.5">
                                            {b.fields.map((f) => (
                                                <span key={f.field} className={`text-[10px] font-mono px-1.5 py-0.5 rounded-sm border ${f.org_level ? "bg-amber-500/15 text-amber-300 border-amber-500/30" : "bg-white/5 text-slate-400 border-white/10"}`}>
                                                    {f.field} · {f.label} {f.org_level ? "(Org)" : ""}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </TabsContent>

                    <TabsContent value="sim" className="pt-3 space-y-3">
                        <p className="text-xs text-slate-500 font-mono">
                            Test the impact of adding new T-codes to this role — reveals only the NEW SoD conflicts introduced, without saving.
                        </p>
                        <div className="flex gap-2">
                            <input
                                data-testid={TID.roleSimulateInput}
                                value={addTcodes}
                                onChange={(e) => setAddTcodes(e.target.value)}
                                placeholder="e.g. FK01 FK02"
                                className="flex-1 bg-[var(--bg-base)] border border-white/10 rounded-sm px-3 py-1.5 text-xs font-mono text-slate-200 focus:border-cyan-500/40 focus:outline-none"
                            />
                            <button
                                data-testid={TID.roleSimulateRunBtn}
                                onClick={runSimulation}
                                disabled={simulating}
                                className="btn-primary sheen inline-flex items-center gap-1.5 px-3 py-1.5 text-xs disabled:opacity-60"
                            >
                                {simulating ? <Loader2 size={13} className="animate-spin" /> : <FlaskConical size={13} />}
                                Run
                            </button>
                        </div>
                        {simResult && (
                            <div className="space-y-2">
                                <div className="flex items-center gap-2 text-xs font-mono">
                                    {simResult.is_safe_to_add ? (
                                        <span className="inline-flex items-center gap-1.5 text-emerald-400"><CheckCircle2 size={14} /> Safe to add — no new conflicts</span>
                                    ) : (
                                        <span className="inline-flex items-center gap-1.5 text-rose-400"><ShieldAlert size={14} /> {simResult.introduced_conflicts.length + simResult.introduced_critical_activities.length} new issue(s) introduced</span>
                                    )}
                                    <SeverityBadge severity={simResult.risk_level_after} />
                                </div>
                                {simResult.introduced_conflicts.map((c) => (
                                    <div key={c.rule_id} className="panel-elevated p-2.5 border-l-2 border-rose-500/50">
                                        <div className="flex items-center gap-2"><SeverityBadge severity={c.severity} /><span className="text-xs text-white">{c.rule_name}</span><FieldConfidenceBadge confidence={c.match_confidence} note={c.field_level_note} /></div>
                                        <p className="text-[11px] text-slate-400 mt-1">{c.description}</p>
                                    </div>
                                ))}
                                {simResult.introduced_critical_activities.map((c) => (
                                    <div key={c.risk_id} className="panel-elevated p-2.5 border-l-2 border-orange-500/50">
                                        <div className="flex items-center gap-2"><SeverityBadge severity={c.severity} /><span className="text-xs text-white">{c.name}</span></div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </TabsContent>

                    <TabsContent value="release" className="pt-3 space-y-2">
                        <p className="text-xs text-slate-500 font-mono">
                            Cross-referenced against the real SAP Fiori Apps Reference Library&rsquo;s own App Status field for every T-code on this role.
                        </p>
                        {loadingRelease ? (
                            <div className="flex items-center gap-2 text-slate-500 text-xs font-mono py-4"><Loader2 size={13} className="animate-spin" /> Loading…</div>
                        ) : !release?.rows?.length ? (
                            <p className="text-sm text-slate-500 font-mono py-4 text-center">This role has no transactions to evaluate.</p>
                        ) : (
                            <>
                                <div className="flex gap-2 flex-wrap">
                                    <span className={`text-[10px] font-mono px-2 py-1 rounded-sm border ${RELEASE_STYLES.Available}`}>{release.summary.available} Available</span>
                                    <span className={`text-[10px] font-mono px-2 py-1 rounded-sm border ${RELEASE_STYLES["Available with Successor"]}`}>{release.summary.with_successor} With Successor</span>
                                    <span className={`text-[10px] font-mono px-2 py-1 rounded-sm border ${RELEASE_STYLES.Deprecated}`}>{release.summary.deprecated} Deprecated</span>
                                    <span className={`text-[10px] font-mono px-2 py-1 rounded-sm border ${RELEASE_STYLES["Not in Fiori Library"]}`}>{release.summary.unknown} Not in Library</span>
                                </div>
                                <div className="space-y-1.5 max-h-[280px] overflow-y-auto">
                                    {release.rows.map((r) => (
                                        <div key={r.tcode} className="flex items-center gap-2 panel px-2.5 py-1.5 rounded-[4px]">
                                            <GitCommitVertical size={12} className="text-slate-500 shrink-0" />
                                            <span className="font-mono text-xs text-cyan-300 shrink-0">{r.tcode}</span>
                                            <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded-sm border shrink-0 ${RELEASE_STYLES[r.status]}`}>{r.status}</span>
                                            {r.apps[0] && <span className="text-[10px] text-slate-500 truncate">{r.apps[0].app_name}</span>}
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                    </TabsContent>

                    <TabsContent value="migration" className="pt-3 space-y-3">
                        <p className="text-xs text-slate-500 font-mono">
                            One-click migration: finds the smallest set of real Fiori Business Catalogs whose
                            member apps cover this role&rsquo;s T-codes, and tags the role with them — purely
                            additive, the T-codes and SoD analysis are never touched.
                        </p>
                        {role.fiori_catalogs?.length > 0 && (
                            <div className="flex items-center gap-1.5 text-[11px] font-mono text-cyan-300">
                                <LayoutGrid size={12} /> Already has {role.fiori_catalogs.length} catalog(s) assigned — re-running is safe and additive.
                            </div>
                        )}
                        <button
                            data-testid={TID.roleMigrationConvertBtn}
                            onClick={convertToCatalog}
                            disabled={migrating}
                            className="btn-primary sheen w-full inline-flex items-center justify-center gap-1.5 py-2 text-xs disabled:opacity-60"
                        >
                            {migrating ? <Loader2 size={13} className="animate-spin" /> : <ArrowRightLeft size={13} />}
                            Convert to Catalog-Based
                        </button>
                        {migrationResult && (
                            <div data-testid={TID.roleMigrationResultPanel} className="space-y-2">
                                <div className="panel-elevated p-2.5 text-[11px] font-mono text-slate-300 flex flex-wrap gap-3">
                                    <span className="text-cyan-300">{migrationResult.coverage_pct}% T-code coverage</span>
                                    <span>{migrationResult.matched_catalogs.length} catalog(s) matched</span>
                                    <span>{migrationResult.uncovered_tcodes.length} T-code(s) unmatched</span>
                                </div>
                                {migrationResult.matched_catalogs.map((c) => (
                                    <div key={c.catalog_id} className="panel p-2.5 rounded-[4px]">
                                        <div className="text-xs text-white">{c.title}</div>
                                        <div className="text-[10px] font-mono text-slate-500 mt-0.5">{c.covered_tcodes.join(", ")}</div>
                                    </div>
                                ))}
                                {migrationResult.uncovered_tcodes.length > 0 && (
                                    <div className="panel p-2.5 rounded-[4px] border-l-2 border-amber-500/40">
                                        <div className="text-[10px] font-mono text-amber-300 uppercase tracking-wider mb-1">No catalog mapping found</div>
                                        <div className="text-[10px] font-mono text-slate-500">{migrationResult.uncovered_tcodes.join(", ")}</div>
                                    </div>
                                )}
                            </div>
                        )}
                    </TabsContent>

                    <TabsContent value="docs" className="pt-3 space-y-3">
                        <p className="text-xs text-slate-500 font-mono">
                            AI-generated (UTSAVA Reasoner) formal documentation based on this role&rsquo;s transactions, authorization objects and SoD analysis. Preview before committing to a download.
                        </p>
                        <div className="flex gap-2">
                            <button
                                data-testid={TID.roleDocBtn("bpd")}
                                onClick={() => previewDoc("bpd")}
                                disabled={!!generatingDoc}
                                className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 panel text-xs font-mono text-slate-300 hover:text-cyan-300 hover:border-cyan-500/30 transition-colors disabled:opacity-50"
                            >
                                {generatingDoc === "bpd" ? <Loader2 size={13} className="animate-spin" /> : <Eye size={13} />}
                                Preview BPD
                            </button>
                            <button
                                data-testid={TID.roleDocBtn("pdd")}
                                onClick={() => previewDoc("pdd")}
                                disabled={!!generatingDoc}
                                className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 panel text-xs font-mono text-slate-300 hover:text-cyan-300 hover:border-cyan-500/30 transition-colors disabled:opacity-50"
                            >
                                {generatingDoc === "pdd" ? <Loader2 size={13} className="animate-spin" /> : <Eye size={13} />}
                                Preview PDD
                            </button>
                        </div>
                        {preview && (
                            <div className="space-y-2">
                                <div className="panel-elevated p-3 max-h-[260px] overflow-y-auto">
                                    <div className="text-[10px] font-mono text-cyan-400 uppercase tracking-wider mb-1.5">{preview.docType.toUpperCase()} Preview</div>
                                    <p className="text-[11px] text-slate-300 leading-relaxed whitespace-pre-line">{preview.content}</p>
                                </div>
                                <button
                                    data-testid={TID.roleDocDownloadBtn}
                                    onClick={downloadPreviewedDoc}
                                    disabled={!!generatingDoc}
                                    className="btn-primary sheen w-full inline-flex items-center justify-center gap-1.5 py-2 text-xs disabled:opacity-60"
                                >
                                    {generatingDoc === `download-${preview.docType}` ? <Loader2 size={13} className="animate-spin" /> : <FileText size={13} />}
                                    Download {preview.docType.toUpperCase()} (.docx)
                                </button>
                            </div>
                        )}
                    </TabsContent>

                    <TabsContent value="diagrams" className="pt-3">
                        <DiagramsTab role={role} />
                    </TabsContent>

                    <TabsContent value="best-practice" className="pt-3">
                        <BestPracticeTab role={role} onUpdated={onUpdated} />
                    </TabsContent>
                </Tabs>
            </DialogContent>
        </Dialog>
    );
}
